import { useRef } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { useBreakpoint, useTheme } from '@hivebrite/design-system/Theme';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper } from 'hb-builder/components/common/Wrapper/WrapperRender';
import { RenderSectionContent } from 'hb-builder/components/RenderComponents/shared/common/Section/components/RenderSectionContent';
import { useIntersectionObserver } from 'hb-builder/components/RenderComponents/shared/common/Section/hooks/useIntersectionObserver';
import { SectionProvider } from 'hb-builder/contexts';
import { useRendererContext } from 'hb-builder/hooks';
import type { RenderComponent } from 'hb-builder/types';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';

import WidthContentBox from './components/WidthContentBox';
import type { WrapperInnerStyleProps } from './style';
import {
  getInnerStyle,
  getInnerStyleWithDynamicComponent,
  getWrapperSectionStyle,
  wrapperInnerStyle,
} from './style';

const componentType = 'section';

const Section: RenderComponent = () => <div />; // never used

const Render = ({
  children,
  item = { id: '', type: '' },
}: RenderProps<DocumentItem<SectionItemAttributes>>) => {
  const { attributes } = item;
  const breakpoint = useBreakpoint();
  const isMobileDevice = useIsMobileDevice();
  const { bodyWidth, isMobilePreview, workspaceConfig } = useRendererContext();
  const theme = useTheme();
  const sectionRef = useRef<HTMLDivElement>(null);

  const forceContentOutsideGrid = attributes?.outsideGrid;

  useIntersectionObserver(sectionRef, item?.id);

  const isMobile =
    isMobilePreview ||
    isMobileDevice ||
    breakpoint === 's' ||
    breakpoint === 'm';

  const isGridSection =
    attributes?.hasColGap &&
    attributes?.dynamicAttributes &&
    attributes?.dynamicAttributes.type !== 'empty';

  return (
    <SectionProvider item={item}>
      {({ ref }) => (
        // @ts-expect-error not typed
        <Wrapper
          isMobilePreview={isMobilePreview}
          ref={sectionRef}
          style={getWrapperSectionStyle({
            attributes,
            isMobile,
            colors: theme?.colors,
          })}
        >
          {attributes?.fullWidth &&
            attributes?.containerBackgroundImage &&
            attributes?.containerBackgroundImageAlt && (
              <span
                role="img"
                aria-label={attributes?.containerBackgroundImageAlt}
              />
            )}
          <WidthContentBox
            attributes={attributes}
            workspaceConfig={workspaceConfig}
            // avoid horizontal scrolling on mobile
            {...(isMobile && { overflow: 'hidden' })}
            {...(!forceContentOutsideGrid && { width: bodyWidth })}
          >
            <WrapperInner
              ref={ref}
              $isMobile={isMobile}
              $minHeight={attributes?.height}
              $mobileDisplay={attributes?.mobileDisplay}
              $verticalAlign={attributes?.verticalAlign}
              data-testid="builder-component-section"
              style={{
                ...(isGridSection
                  ? getInnerStyleWithDynamicComponent({ bodyWidth })
                  : getInnerStyle({
                      attributes,
                      colors: theme?.colors,
                      componentType,
                      isMobile,
                      isSmallScreen: workspaceConfig.isSmallScreen,
                    })),
              }}
            >
              {!isGridSection &&
                !attributes?.fullWidth &&
                attributes?.containerBackgroundImage &&
                attributes?.containerBackgroundImageAlt && (
                  <span
                    role="img"
                    aria-label={attributes.containerBackgroundImageAlt}
                  />
                )}
              <RenderSectionContent
                attributes={item?.attributes}
                isGridSection={isGridSection}
                isMobile={isMobile}
                ref={ref}
              >
                {children}
              </RenderSectionContent>
            </WrapperInner>
          </WidthContentBox>
        </Wrapper>
      )}
    </SectionProvider>
  );
};

Section.componentName = 'Section';
Section.Render = Render;

export default Section;

export const WrapperInner = styled(Box)<WrapperInnerStyleProps>`
  ${wrapperInnerStyle}
`;
